import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Edit2, Trash2, X, Copy, Plus, Loader, AlertTriangle,
  RefreshCw, Image as ImageIcon, ChevronDown, ArrowLeft,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { cmsFetch } from '../lib/cmsApi';
import CMSAuth from '../components/CMSAuth';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CATEGORIES = ['All', 'Footwear', 'Apparel', 'Backpacks', 'Bags', 'Tech', 'Hydration', 'Weather', 'Recovery', 'Extras'];
const TIERS = ['All', 'Budget', 'Mid', 'Premium'];
const WALK_TYPE_TAGS = ['beach','coastal','urban','hillside','food','nature','architecture','evening','loop','photography','historic','premium'];
const FUNCTION_TAGS = ['footwear','bag','hydration','sun','weather','tech','recovery','extras','comfort'];

// Detect retailer from a URL.
const detectRetailer = (url) => {
  const u = (url || '').toLowerCase();
  if (u.includes('amazon.com') || u.includes('amzn.to')) return 'amazon';
  if (u.includes('rei.com')) return 'rei';
  return 'other';
};

// Slug "doubling" detector: brand appears twice at the start. Surfaces the known ingest awkwardness.
const isSlugDoubled = (slug, brand) => {
  if (!slug || !brand) return false;
  const b = brand.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  if (!b) return false;
  return slug.toLowerCase().startsWith(`${b}-${b}`);
};

const tierColor = (tier) =>
  tier === 'Budget' ? 'bg-green-100 text-green-800' :
  tier === 'Mid' ? 'bg-blue-100 text-blue-800' :
  tier === 'Premium' ? 'bg-amber-100 text-amber-800' :
  'bg-gray-100 text-gray-700';

// ──────────────────────────────────────────────────────────────────────────
// GearFormModal — reusable preview/edit form
// ──────────────────────────────────────────────────────────────────────────

const GearFormModal = ({ doc: initialDoc, isNew, onSave, onCancel, saving }) => {
  const [doc, setDoc] = useState(initialDoc);
  const [imageInputOpen, setImageInputOpen] = useState(false);
  const [imageInput, setImageInput] = useState('');

  const update = (patch) => setDoc(prev => ({ ...prev, ...patch }));
  const toggleWtt = (tag) => {
    const set = new Set(doc.walk_type_tags || []);
    if (set.has(tag)) set.delete(tag); else set.add(tag);
    update({ walk_type_tags: Array.from(set) });
  };

  const priceMissing = !doc.price || doc.price === 0;
  const slugDoubled = isSlugDoubled(doc.slug, doc.brand);

  const onReplaceImage = () => {
    if (!imageInput.trim()) return;
    update({ images: [imageInput.trim()] });
    setImageInput('');
    setImageInputOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{isNew ? 'Confirm New Gear' : `Edit ${initialDoc.name}`}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Image */}
          <div>
            <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              {doc.images?.[0] ? (
                <img src={doc.images[0]} alt={doc.name} className="w-full h-full object-contain p-4" />
              ) : (
                <div className="text-gray-400 flex flex-col items-center"><ImageIcon className="w-12 h-12 mb-1" /><span className="text-sm">No image</span></div>
              )}
            </div>
            {!imageInputOpen ? (
              <button type="button" onClick={() => setImageInputOpen(true)} className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Replace image
              </button>
            ) : (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  placeholder="Paste image URL"
                  className="flex-1 rounded-md border-gray-300 text-sm"
                />
                <button type="button" onClick={onReplaceImage} className="px-3 py-1 text-sm bg-blue-600 text-white rounded">Use</button>
                <button type="button" onClick={() => { setImageInputOpen(false); setImageInput(''); }} className="px-3 py-1 text-sm text-gray-600">Cancel</button>
              </div>
            )}
          </div>

          {/* Fields */}
          <div className="space-y-3 text-sm">
            <div>
              <label className="block font-medium text-gray-700 mb-1">Name</label>
              <input value={doc.name || ''} onChange={(e) => update({ name: e.target.value })} className="w-full rounded-md border-gray-300" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Brand</label>
              <input value={doc.brand || ''} onChange={(e) => update({ brand: e.target.value })} className="w-full rounded-md border-gray-300" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Slug</label>
              <input value={doc.slug || ''} onChange={(e) => update({ slug: e.target.value })} className="w-full rounded-md border-gray-300 font-mono text-xs" />
              {slugDoubled && (
                <p className="mt-1 text-xs text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Slug appears to repeat the brand (e.g. "{doc.slug.slice(0, 30)}…"). Consider editing before saving.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Price</label>
                <input type="number" step="0.01" value={doc.price ?? ''} onChange={(e) => update({ price: parseFloat(e.target.value) || 0 })} className="w-full rounded-md border-gray-300" />
                {priceMissing && (
                  <p className="mt-1 text-xs text-amber-700 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    Price could not be extracted — please enter manually
                  </p>
                )}
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Price Tier</label>
                <select value={doc.price_tier || 'Mid'} onChange={(e) => update({ price_tier: e.target.value })} className="w-full rounded-md border-gray-300">
                  <option value="Budget">Budget</option>
                  <option value="Mid">Mid</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Category</label>
              <input value={doc.category || ''} onChange={(e) => update({ category: e.target.value })} className="w-full rounded-md border-gray-300" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Function tag</label>
              <select value={doc.function_tag || ''} onChange={(e) => update({ function_tag: e.target.value })} className="w-full rounded-md border-gray-300">
                <option value="">(none)</option>
                {FUNCTION_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Walk type tags</label>
              <div className="flex flex-wrap gap-1.5">
                {WALK_TYPE_TAGS.map(tag => {
                  const selected = (doc.walk_type_tags || []).includes(tag);
                  return (
                    <button key={tag} type="button" onClick={() => toggleWtt(tag)} className={`px-2 py-0.5 rounded-full text-xs font-medium border ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                      {tag.charAt(0).toUpperCase() + tag.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Ben's Take</label>
              <textarea rows={2} value={doc.bens_take || ''} onChange={(e) => update({ bens_take: e.target.value })} className="w-full rounded-md border-gray-300" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Full review</label>
              <textarea rows={4} value={doc.full_review || ''} onChange={(e) => update({ full_review: e.target.value })} className="w-full rounded-md border-gray-300" />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Key features (comma-separated)</label>
              <input
                value={(doc.key_features || []).join(', ')}
                onChange={(e) => update({ key_features: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full rounded-md border-gray-300"
              />
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Affiliate URL</label>
              <input value={doc.affiliate_url || ''} onChange={(e) => update({ affiliate_url: e.target.value })} className="w-full rounded-md border-gray-300 font-mono text-xs" />
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(doc)} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? (<><Loader className="w-4 h-4 animate-spin mr-2" />Saving…</>) : 'Confirm & Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// CMSGearPage
// ──────────────────────────────────────────────────────────────────────────

const CMSGearPage = () => {
  const { isAuthenticated } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterTier, setFilterTier] = useState('All');

  const [ingestUrl, setIngestUrl] = useState('');
  const [ingesting, setIngesting] = useState(false);

  const [previewDoc, setPreviewDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const [backfilling, setBackfilling] = useState(false);
  const [status, setStatus] = useState(null);   // { kind: 'success'|'error'|'warning', message }

  const fetchTimer = useRef(null);

  useEffect(() => { setHasAccess(isAuthenticated); }, [isAuthenticated]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'All') params.set('category', filterCategory);
      if (search.trim().length >= 2) params.set('search', search.trim());
      const res = await fetch(`${BACKEND}/api/gear?${params}`);
      const data = await res.json();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  // Debounced refetch on search/category change
  useEffect(() => {
    if (!hasAccess) return;
    clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(fetchItems, 250);
    return () => clearTimeout(fetchTimer.current);
  }, [hasAccess, search, filterCategory]);

  // Client-side tier filter (cheap with 30 items)
  const visibleItems = useMemo(() => {
    if (filterTier === 'All') return items;
    return items.filter(i => i.price_tier === filterTier);
  }, [items, filterTier]);

  const flashStatus = (kind, message, ms = 4000) => {
    setStatus({ kind, message });
    setTimeout(() => setStatus(null), ms);
  };

  // ── Ingest from URL ─────────────────────────────────────────────
  const handleIngest = async () => {
    if (!ingestUrl.trim()) return;
    setIngesting(true);
    setStatus(null);
    try {
      const retailer = detectRetailer(ingestUrl);
      const resp = await cmsFetch('/api/cms/gear/ingest', {
        method: 'POST',
        body: JSON.stringify({ source_url: ingestUrl.trim(), retailer }),
      });
      if (resp.status === 409) {
        const body = await resp.json();
        const existing = body?.detail?.existing;
        flashStatus(
          'warning',
          <>This item already exists in your gear database. <Link to={`/gear/${existing?.slug}`} target="_blank" className="underline">View existing &rarr;</Link></>
        );
        return;
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        flashStatus('error', `Ingest failed: ${body?.detail || resp.status}`);
        return;
      }
      const data = await resp.json();
      setPreviewDoc(data.document);
      setIngestUrl('');
    } catch (e) {
      flashStatus('error', `Ingest error: ${e.message}`);
    } finally {
      setIngesting(false);
    }
  };

  // ── Confirm new gear save ───────────────────────────────────────
  const handleConfirmNew = async (doc) => {
    setSaving(true);
    try {
      const resp = await cmsFetch('/api/cms/gear/ingest/confirm', {
        method: 'POST',
        body: JSON.stringify(doc),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        flashStatus('error', `Save failed: ${body?.detail || resp.status}`);
        return;
      }
      setPreviewDoc(null);
      flashStatus('success', `Gear "${doc.name}" saved.`);
      fetchItems();
    } finally {
      setSaving(false);
    }
  };

  // ── Edit existing gear save ─────────────────────────────────────
  const handleEditSave = async (doc) => {
    setSaving(true);
    try {
      const resp = await cmsFetch(`/api/cms/gear/${doc.slug}`, {
        method: 'PUT',
        body: JSON.stringify(doc),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        flashStatus('error', `Update failed: ${body?.detail || resp.status}`);
        return;
      }
      setEditingDoc(null);
      flashStatus('success', `Updated "${doc.name}".`);
      fetchItems();
    } finally {
      setSaving(false);
    }
  };

  // ── Delete with walk cleanup ────────────────────────────────────
  const handleDelete = async (item) => {
    const ok = window.confirm(`Delete "${item.name}"? This will remove it from all walks that link to it.`);
    if (!ok) return;
    try {
      const resp = await cmsFetch(`/api/cms/gear/${item.slug}`, { method: 'DELETE' });
      if (!resp.ok) {
        flashStatus('error', `Delete failed: ${resp.status}`);
        return;
      }
      const body = await resp.json();
      flashStatus('success', `Deleted "${item.name}". ${body.walks_updated || 0} walk(s) updated.`);
      fetchItems();
    } catch (e) {
      flashStatus('error', `Delete error: ${e.message}`);
    }
  };

  // ── Backfill images ─────────────────────────────────────────────
  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const resp = await cmsFetch('/api/cms/gear/backfill-images', { method: 'POST' });
      if (!resp.ok) {
        flashStatus('error', `Backfill failed: ${resp.status}`);
        return;
      }
      const data = await resp.json();
      flashStatus(
        'success',
        `Backfill complete — updated ${data.updated}, skipped ${data.skipped_no_asin} (no ASIN), failed ${data.failed}.`,
        8000,
      );
      fetchItems();
    } catch (e) {
      flashStatus('error', `Backfill error: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text || '').then(() => flashStatus('success', 'Copied to clipboard', 1500));
  };

  if (!hasAccess) return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/cms/dashboard" className="text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-2xl font-bold text-gray-900">Gear Management</h1>
            <span className="text-sm text-gray-500">{items.length} items</span>
          </div>
          <Button onClick={handleBackfill} disabled={backfilling} variant="outline" className="flex items-center gap-2">
            {backfilling ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {backfilling ? 'Backfilling…' : 'Backfill Images'}
          </Button>
        </div>

        {/* Status banner */}
        {status && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            status.kind === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            status.kind === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-amber-50 text-amber-800 border border-amber-200'
          }`}>
            {status.message}
          </div>
        )}

        {/* Add new gear panel */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Add New Gear</h2>
          <p className="text-sm text-gray-500 mb-3">Paste an Amazon or REI product URL — we fetch the data and AI-enrich it for you.</p>
          <div className="flex gap-2">
            <input
              type="url"
              value={ingestUrl}
              onChange={(e) => setIngestUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !ingesting && handleIngest()}
              placeholder="https://www.amazon.com/dp/B0… or https://rei.com/product/…"
              className="flex-1 rounded-md border-gray-300 text-sm"
            />
            <Button onClick={handleIngest} disabled={ingesting || !ingestUrl.trim()} className="bg-blue-600 hover:bg-blue-700 text-white">
              {ingesting ? (<><Loader className="w-4 h-4 animate-spin mr-2" />Fetching…</>) : (<><Plus className="w-4 h-4 mr-2" />Ingest from URL</>)}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or brand…"
              className="w-full pl-9 rounded-md border-gray-300 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Category</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="rounded-md border-gray-300 text-sm">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Tier</label>
            <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="rounded-md border-gray-300 text-sm">
              {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Inventory table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name / Brand</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Walk Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400 text-sm">Loading…</td></tr>
                ) : visibleItems.length === 0 ? (
                  <tr><td colSpan={9} className="py-12 text-center text-gray-400 text-sm">No gear matches the filters.</td></tr>
                ) : visibleItems.map(item => {
                  const wtt = item.walk_type_tags || [];
                  return (
                    <tr key={item.slug} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt="" className="w-10 h-10 object-contain bg-gray-50 rounded border" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-gray-400"><ImageIcon className="w-4 h-4" /></div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.brand}</p>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{item.category}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">${item.price}</td>
                      <td className="px-3 py-2">
                        {item.price_tier && <Badge className={tierColor(item.price_tier)}>{item.price_tier}</Badge>}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{item.rating ?? '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {wtt.slice(0, 2).map(t => (
                            <span key={t} className="px-1.5 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{t}</span>
                          ))}
                          {wtt.length > 2 && <span className="text-xs text-gray-500">+{wtt.length - 2} more</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {item.affiliate_url ? (
                          <div className="flex items-center gap-1 max-w-[200px]">
                            <a href={item.affiliate_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate font-mono">
                              {item.affiliate_url.replace(/^https?:\/\//, '')}
                            </a>
                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(item.affiliate_url); }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditingDoc(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(item)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {previewDoc && (
        <GearFormModal
          doc={previewDoc}
          isNew={true}
          saving={saving}
          onSave={handleConfirmNew}
          onCancel={() => setPreviewDoc(null)}
        />
      )}
      {editingDoc && (
        <GearFormModal
          doc={editingDoc}
          isNew={false}
          saving={saving}
          onSave={handleEditSave}
          onCancel={() => setEditingDoc(null)}
        />
      )}
    </div>
  );
};

export default CMSGearPage;
